import { GoldOutlined } from '@ant-design/icons';
import { message as antdMessage, Modal, Tabs, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RowData } from '../components/KaratCalculator';
import type { StockState as ApiStockState } from '../contexts/ApiContext';
import { InventorySummaryRow, useApi } from '../contexts/ApiContext';
import type { IncomingMetal, IncomingMetalReport, StockInventoryItem } from '../domain/metals';

import { generateMeltingRecordPdf, MeltingRecordData } from '../utils/meltingRecordPdf';

const { Title, Text } = Typography;

import IncomingByMetalContent from '../components/metals/IncomingByMetalContent';
import IncomingContent from '../components/metals/IncomingContent';
import MeltingContent from '../components/metals/MeltingContent';
import PurchaseContent from '../components/metals/PurchaseContent';
import PurchasesListContent from '../components/metals/PurchasesListContent';
import StockContent from '../components/metals/StockContent';
import StockInvContent from '../components/metals/StockInvContent';
import { groupInventoryForProcessing } from '../domain/metals/grouping';
import { validateContractDates as validateContractDatesDomain } from '../domain/metals/validation';

// extracted components are imported above

const Metals = () => {
    const { metals } = useApi();
    const { t } = useTranslation();
    const [messageApi, contextHolder] = antdMessage.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();
    const [data, setData] = useState<IncomingMetal[]>([]);
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [summary, setSummary] = useState<IncomingMetalReport | null>(null);

    const [stockData, setStockData] = useState<ApiStockState[]>([]);
    const [stockExchangeRate, setStockExchangeRate] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState('incoming');
    const [byMetalRefreshKey, setByMetalRefreshKey] = useState(0);
    const [processRows, setProcessRows] = useState<RowData[]>([]);
    const [meltingData, setMeltingData] = useState<RowData[]>([]);
    const [melts, setMelts] = useState<Array<{ id: number; date: string }>>([]);
    const [selectedMelt, setSelectedMelt] = useState<number | null>(null);
    const [loadedRows, setLoadedRows] = useState<RowData[]>([]);
    const [afterMass, setAfterMass] = useState<number | undefined>(undefined);
    const [afterKarat, setAfterKarat] = useState<number | undefined>(undefined);
    const [losses, setLosses] = useState<
        { lossGr: number; lossPercent: number; totalLossPercent: number } | undefined
    >(undefined);

    // Track tab change source: 'user' for manual tab clicks, 'program' for button-triggered changes
    const [tabChangeSource, setTabChangeSource] = useState<'user' | 'program'>('user');
    // Raised by every new purchase, so the Purchases tab reloads its list.
    const [purchasesRefreshKey, setPurchasesRefreshKey] = useState(0);

    // Stock inventories tab state
    const [stockInvDates, setStockInvDates] = useState<string[]>([]);
    const [stockInvItems, setStockInvItems] = useState<Record<string, StockInventoryItem[]>>({});
    const [stockInvSummary, setStockInvSummary] = useState<
        Record<string, { date: string; grouped: InventorySummaryRow[] }>
    >({});
    const [stockInvSelectedDate, setStockInvSelectedDate] = useState<string | null>(null);
    const [stockInvSelectedRowKeys, setStockInvSelectedRowKeys] = useState<React.Key[]>([]);
    const [mergeModalVisible, setMergeModalVisible] = useState(false);
    const [mergeDate, setMergeDate] = useState<Dayjs | null>(null);

    const [mergeMinDate, setMergeMinDate] = useState<Dayjs | null>(null);

    const loadData = useCallback(
        async (dateValue?: string | null) => {
            const dateToUse = dateValue ?? selectedDate;
            if (!dateToUse) return;
            setLoading(true);
            const dateStr = dayjs(dateToUse).format('YYYY-MM-DD');
            const result = await metals.getIncoming(dateStr);
            if (result.success) {
                setData((result.data?.metals ?? []) as IncomingMetal[]);
            } else {
                messageApi.error(result.error ?? 'Failed to load metals');
            }

            const summaryRes = await metals.getIncomingReport(dateStr);
            if (summaryRes.success) {
                setSummary((summaryRes.data ?? null) as IncomingMetalReport | null);
            } else {
                messageApi.error(summaryRes.error ?? 'Failed to load summary');
            }
            setLoading(false);
        },
        [metals, selectedDate, messageApi],
    );

    const loadStock = useCallback(async () => {
        const res = await metals.getStock();
        if (res.success) {
            setStockData(res.data?.items ?? []);
            setStockExchangeRate(res.data?.exchangeRate ?? null);
        } else {
            messageApi.error(res.error ?? t('metals.loadStockFailed'));
        }
    }, [metals, messageApi, t]);

    const loadStockInventoryDates = useCallback(async () => {
        const res = await metals.getInventoryDates();
        if (res.success) {
            setStockInvDates(res.data?.dates ?? []);
        }
    }, [metals]);

    const loadStockInventoryData = useCallback(
        async (date: string) => {
            const [itemsRes, summaryRes] = await Promise.all([
                metals.getInventory(date),
                metals.getInventoryReport(date),
            ]);
            if (itemsRes.success) {
                setStockInvItems(prev => ({
                    ...prev,
                    [date]: (itemsRes.data?.items ?? []) as StockInventoryItem[],
                }));
            }
            if (summaryRes.success) {
                setStockInvSummary(prev => ({
                    ...prev,
                    [date]: summaryRes.data ?? { date, grouped: [] },
                }));
            }
        },
        [metals],
    );

    const loadMelts = useCallback(async () => {
        try {
            const res = await metals.getMelts();
            if (res.success) {
                setMelts(res.data?.items ?? []);
            } else {
                messageApi.error(res.error ?? t('metals.loadMeltsFailed'));
            }
        } catch (error) {
            console.error('loadMelts error:', error);
            messageApi.error(t('metals.loadMeltsFailed'));
        }
    }, [metals, messageApi, t]);

    useEffect(() => {
        const init = async () => {
            const res = await metals.getIncomingDates();
            if (res.success) {
                setDates(res.data?.dates ?? []);
                const first = (res.data?.dates ?? [])[0];
                setSelectedDate(first);
                if (first) {
                    const dateStr = dayjs(first).format('YYYY-MM-DD');
                    setLoading(true);
                    const result = await metals.getIncoming(dateStr);
                    if (result.success) {
                        setData((result.data?.metals ?? []) as IncomingMetal[]);
                    } else {
                        messageApi.error(result.error ?? 'Failed to load metals');
                    }

                    const summaryRes = await metals.getIncomingReport(dateStr);
                    if (summaryRes.success) {
                        setSummary((summaryRes.data ?? null) as IncomingMetalReport | null);
                    } else {
                        messageApi.error(summaryRes.error ?? 'Failed to load summary');
                    }
                    setLoading(false);
                }
            } else {
                messageApi.error(res.error ?? 'Failed to load dates');
            }
        };
        init();
    }, [metals, messageApi]);

    useEffect(() => {
        loadStock();
        loadStockInventoryDates();
        loadMelts();
    }, [loadStock, loadStockInventoryDates, loadMelts]);

    const handleDateChange = (value: dayjs.Dayjs | null) => {
        const dateStr = value ? value.format('YYYY-MM-DD') : null;
        setSelectedDate(dateStr);
        loadData(dateStr);
    };

    const handleDelete = async (item: Record<string, unknown>) => {
        console.log('handleDelete called with item:', item);
        modal.confirm({
            title: t('metals.reactivateContract') || 'Reactivate Contract',
            content:
                t('metals.confirmReactivate') ||
                "This will reactivate the contract, set its status to '!!' and date to 2000-01-01, and remove it from inventory. Are you sure?",
            okType: 'danger',
            centered: true,
            maskClosable: false,
            onOk: async () => {
                try {
                    console.log('Calling reactivateContract with:', item.contract_number);
                    const result = await metals.reactivateContract(String(item.contract_number));
                    console.log('Reactivation result:', result);

                    if (result.success) {
                        messageApi.success(result.data?.message);
                        // Step 3: Recalculate summarization by reloading data and summary
                        if (selectedDate) loadData(selectedDate);
                    } else {
                        messageApi.error(result.error ?? 'Reactivation failed');
                    }
                } catch (error) {
                    console.error('Reactivation error:', error);
                    messageApi.error(t('metals.reactivationFailed'));
                }
            },
            onCancel: () => {
                console.log('Modal cancelled');
            },
        });
        console.log('Modal.confirm called');
    };

    const handleAddToInventory = async () => {
        if (!selectedDate) {
            messageApi.error(t('metals.selectDateFirst'));
            return;
        }

        try {
            const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
            const result = await metals.addToInventory(dateStr);

            if (result.success) {
                const msg = result.data?.message ?? 'Contracts added to inventory successfully.';
                messageApi.success(msg);
            } else {
                messageApi.error(result.error ?? 'Failed to add contracts to inventory');
            }
        } catch (error) {
            console.error('Add to inventory error:', error);
            messageApi.error(t('metals.addToStockFailed'));
        }
    };

    const handleStockInvExpand = async (expanded: boolean, record: { date: string }) => {
        if (expanded) {
            setStockInvSelectedDate(record.date);
            await loadStockInventoryData(record.date);
        }
    };

    const handleMeltInventoryDates = async () => {
        let allRows: RowData[] = [];
        for (const d of stockInvSelectedRowKeys as string[]) {
            if (!stockInvItems[d]) {
                await loadStockInventoryData(d);
            }
            const items = stockInvItems[d] ?? [];
            allRows = allRows.concat(groupInventoryForProcessing(items));
        }
        // Group again to merge across dates
        const merged = groupInventoryForProcessing(
            allRows.map(r => ({
                metal_type: r.metalType === 'Silver' ? 'ARGINT' : 'AUR',
                fineness: r.karat,
                gross_quantity: r.grams,
            })),
        );
        setProcessRows(merged);
        setTabChangeSource('program');
        setActiveTab('melting');
    };

    const validateContractDates = async (selectedDates: string[]): Promise<Dayjs | null> => {
        let maxContract: Dayjs | null = null;

        for (const d of selectedDates) {
            if (!stockInvItems[d]) {
                await loadStockInventoryData(d);
            }
            const items = stockInvItems[d] ?? [];
            const { maxContract: currentMax, errorKey } = validateContractDatesDomain(items, d);
            if (errorKey) {
                messageApi.error(t(errorKey));
                return null;
            }
            if (currentMax && (!maxContract || currentMax.isAfter(maxContract))) {
                maxContract = currentMax;
            }
        }
        return maxContract;
    };

    const openMergeModal = async () => {
        const selectedDates = stockInvSelectedRowKeys as string[];
        const maxContract = await validateContractDates(selectedDates);

        if (maxContract === null) {
            return; // Error already shown by validateContractDates
        }

        setMergeMinDate(maxContract);
        setMergeDate(maxContract);
        setMergeModalVisible(true);
    };

    const handleMergeConfirm = async () => {
        if (!mergeDate) {
            messageApi.error(t('metals.selectDate'));
            return;
        }
        modal.confirm({
            title: t('metals.confirmMerge'),
            centered: true,
            onOk: () => {
                const newDateStr = mergeDate.format('YYYY-MM-DD');
                return metals
                    .mergeInventoryDates(stockInvSelectedRowKeys as string[], newDateStr)
                    .then(res => {
                        if (res.success) {
                            messageApi.success(res.data?.message ?? 'Merged successfully');
                            setMergeModalVisible(false);
                            setMergeDate(null);
                            setStockInvSelectedRowKeys([]);
                            loadStockInventoryDates();
                            return loadStockInventoryData(newDateStr);
                        } else {
                            messageApi.error(res.error ?? t('metals.mergeFailed'));
                            throw new Error(res.error ?? t('metals.mergeFailed'));
                        }
                    });
            },
        });
    };

    const handleLoadStock = async () => {
        try {
            const result = await metals.getMetalStocks();
            if (result.success && result.data?.items) {
                // Convert metal stocks to RowData format
                const stockData: RowData[] = result.data.items.map(stock => ({
                    metalType: stock.MetalType === 'AUR' ? 'Gold' : 'Silver',
                    karat: Number(stock.Karat),
                    grams: Number(stock.totalMass),
                    pmp: Number(stock.avgPmp),
                }));
                setProcessRows(stockData);
                // Clear selected melt so KaratCalculator shows the loaded stock data
                setSelectedMelt(null);
                setLoadedRows([]);
                setAfterMass(undefined);
                setAfterKarat(undefined);
                messageApi.success(t('metals.stockLoaded', { count: stockData.length }));
            } else {
                messageApi.error(result.error ?? 'Failed to load metal stocks');
            }
        } catch (error) {
            console.error('Load stock error:', error);
            messageApi.error(t('metals.stockLoadFailed'));
        }
    };

    const handleSelectMelt = async (id: number) => {
        const res = await metals.getMeltDetails(id);
        if (res.success) {
            setSelectedMelt(id);
            const rows = res.data?.details?.map(d => ({
                metalType: d.metalType === 'AUR' ? 'Gold' : 'Silver',
                karat: Number(d.karat),
                grams: Number(d.quantityToMelt),
                quantityToMelt: Number(d.quantityToMelt),
                pmp: Number(d.pmp) || 0, // Use actual PMP from melt details
            })) as RowData[];
            setLoadedRows(rows);
            setAfterMass(Number(res.data?.melting.afterMass));
            setAfterKarat(Number(res.data?.melting.afterPurity));
        }
    };

    const handleSaveMelt = async () => {
        if (selectedMelt) {
            const res = await metals.updateMelt(selectedMelt, {
                afterMass: afterMass ?? 0,
                afterPurity: afterKarat ?? 0,
            });
            if (res.success) {
                messageApi.success(res.data?.message ?? t('metals.saved'));
                loadMelts();
            } else {
                messageApi.error(res.error ?? t('metals.saveFailed'));
            }
        }
    };

    const handleMelt = async () => {
        if (!meltingData || meltingData.length === 0) {
            messageApi.warning(t('metals.enterQuantitiesFirst'));
            return;
        }

        // Validate that all melting quantities are valid
        const validMeltingData = meltingData.filter(
            item => item.quantityToMelt && item.quantityToMelt > 0 && item.karat && item.metalType,
        );

        if (validMeltingData.length === 0) {
            messageApi.warning(t('metals.enterValidQuantities'));
            return;
        }

        console.log('🔥 FRONTEND: Starting melting process with data:', validMeltingData);
        validMeltingData.forEach((item, index) => {
            console.log(
                `  ${index + 1}. ${item.metalType} ${item.karat}k - Stock: ${item.grams}, Quantity to melt: ${item.quantityToMelt}, PMP: ${item.pmp}`,
            );
        });

        modal.confirm({
            title: t('metals.confirmMelt'),
            content: t('metals.confirmMeltMessage', { count: validMeltingData.length }),
            okType: 'primary',
            centered: true,
            onOk: async () => {
                try {
                    const meltRequestData = validMeltingData.map(item => ({
                        metalType: item.metalType!,
                        karat: item.karat!,
                        quantityToMelt: item.quantityToMelt!,
                        pmp: item.pmp ?? 0,
                    }));

                    console.log('🔥 FRONTEND: Sending melt request with data:', meltRequestData);

                    const result = await metals.meltMetals(meltRequestData);

                    if (result.success) {
                        messageApi.success(
                            result.data?.message ?? 'Melting completed successfully',
                        );
                        // Reload stock data to reflect changes
                        handleLoadStock();
                        // Reload melts list to show the new melting
                        loadMelts();
                        // Clear melting data
                        setMeltingData([]);
                        setProcessRows([]);
                    } else {
                        console.error('🔥 FRONTEND: Melting failed:', result.error);
                        messageApi.error(result.error ?? 'Melting failed');
                    }
                } catch (error) {
                    console.error('🔥 FRONTEND: Melting error:', error);
                    messageApi.error(t('metals.meltingFailed'));
                }
            },
        });
    };

    const handleGenerateMeltingRecord = async () => {
        if (!selectedMelt && (!meltingData || meltingData.length === 0)) {
            messageApi.warning(t('metals.noMeltingRecordData'));
            return;
        }

        try {
            const dataToUse = selectedMelt ? loadedRows : meltingData;
            const meltingRecordData: MeltingRecordData = {
                recordNumber: selectedMelt ? `#${selectedMelt}` : undefined,
                date: new Date().toLocaleDateString('ro-RO'),
                location: 'Str. Frantz Liszt 11, Targu Mures',
                meltingData: dataToUse,
                afterMass,
                afterKarat,
                losses: losses
                    ? {
                          lossGr: losses.lossGr,
                          lossPercent: losses.lossPercent,
                          totalLossPercent: losses.totalLossPercent,
                      }
                    : undefined,
                observations:
                    'Proces verbal pentru topirea materialelor de aur și argint conform evidențelor.',
                totalPages: 2,
                totalAnnexes: 1,
            };

            await generateMeltingRecordPdf(meltingRecordData);
            messageApi.success(t('metals.recordGenerated'));
        } catch (error) {
            console.error('Error generating process record:', error);
            messageApi.error(t('metals.recordFailed'));
        }
    };

    const incomingContent = (
        <IncomingContent
            t={t}
            dates={dates}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onAddToInventory={handleAddToInventory}
            onDelete={handleDelete}
            data={data}
            loading={loading}
            summary={summary}
        />
    );

    const stockContent = (
        <StockContent t={t} stockData={stockData} exchangeRate={stockExchangeRate} />
    );

    const stockInvContent = (
        <StockInvContent
            stockInvDates={stockInvDates}
            stockInvSelectedRowKeys={stockInvSelectedRowKeys}
            setStockInvSelectedRowKeys={setStockInvSelectedRowKeys}
            stockInvItems={stockInvItems}
            handleStockInvExpand={handleStockInvExpand}
            setStockInvSelectedDate={setStockInvSelectedDate}
            loadStockInventoryData={loadStockInventoryData}
            handleMeltInventoryDates={handleMeltInventoryDates}
            openMergeModal={openMergeModal}
            stockInvSelectedDate={stockInvSelectedDate}
            stockInvSummary={stockInvSummary}
            mergeModalVisible={mergeModalVisible}
            setMergeModalVisible={setMergeModalVisible}
            mergeDate={mergeDate}
            setMergeDate={setMergeDate}
            mergeMinDate={mergeMinDate}
            handleMergeConfirm={handleMergeConfirm}
            t={t}
        />
    );

    const meltingContent = (
        <MeltingContent
            t={t}
            melts={melts}
            selectedMelt={selectedMelt}
            onSelectMelt={handleSelectMelt}
            onLoadStock={handleLoadStock}
            processRows={processRows}
            loadedRows={loadedRows}
            setMeltingData={setMeltingData}
            rowsEditable={!selectedMelt}
            afterMass={afterMass}
            afterKarat={afterKarat}
            onAfterValuesChange={(m, k) => {
                setAfterMass(m);
                setAfterKarat(k);
            }}
            onLossesChange={setLosses}
            onSaveMelt={handleSaveMelt}
            onGenerateMeltingRecord={handleGenerateMeltingRecord}
            onMelt={handleMelt}
            meltingData={meltingData}
        />
    );

    // A purchase put into stock shows on the Stock and Stock Inventories tabs.
    const handleAddedToStock = () => {
        loadStock();
        loadStockInventoryDates();
    };

    const handlePurchased = () => {
        handleAddedToStock();
        setPurchasesRefreshKey(key => key + 1);
    };

    // Handle tab changes with reload logic
    const handleTabChange = (key: string) => {
        if (tabChangeSource === 'user') {
            // Reload content for each tab when user manually switches
            if (key === 'incoming') {
                loadData();
                setByMetalRefreshKey(previous => previous + 1);
            } else if (key === 'stockinv') {
                loadStockInventoryDates();
            } else if (key === 'stock') {
                loadStock();
            } else if (key === 'melting') {
                loadMelts();
            }
        }
        setActiveTab(key);
        setTabChangeSource('user'); // Reset source to user for next change
    };

    return (
        <div>
            {contextHolder}
            {modalContextHolder}
            <div
                className="page-header"
                style={{ '--page-header-accent': '#b45309' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <GoldOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('metals.title')}
                        </Title>
                        <Text type="secondary">{t('metals.subtitle')}</Text>
                    </div>
                </div>
            </div>
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                    {
                        key: 'incoming',
                        label: t('metals.tabIncoming'),
                        children: (
                            <Tabs
                                type="card"
                                items={[
                                    {
                                        key: 'byMetal',
                                        label: t('metals.byMetal.tab'),
                                        children: (
                                            <IncomingByMetalContent
                                                t={t}
                                                refreshKey={byMetalRefreshKey}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'modify',
                                        label: t('metals.tabModifyInventory'),
                                        children: incomingContent,
                                    },
                                ]}
                            />
                        ),
                    },
                    {
                        key: 'stockinv',
                        label: t('metals.tabStockInventories'),
                        children: stockInvContent,
                    },
                    {
                        key: 'melting',
                        label: t('metals.tabMelting'),
                        children: meltingContent,
                    },
                    {
                        key: 'purchase',
                        label: t('metals.purchase.tab'),
                        children: <PurchaseContent onPurchased={handlePurchased} />,
                    },
                    {
                        key: 'purchases',
                        label: t('metals.purchases.tab'),
                        children: (
                            <PurchasesListContent
                                refreshKey={purchasesRefreshKey}
                                onAddedToStock={handleAddedToStock}
                            />
                        ),
                    },
                    { key: 'stock', label: t('metals.tabStock'), children: stockContent },
                ]}
            />
        </div>
    );
};

export default Metals;
